import { useUpdateFields } from './useUpdateFields'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { NewDeal, Product } from 'types'
import { useCreateTimeline } from '../useCreateTimeline'
import { useQueryClient } from '@tanstack/react-query'
import { updateDealTimelineData } from 'components/Timeline/createTimelineData'
import { useActiveUser } from 'api/slack/useActiveUser'
import { useSyncProducts } from './useSyncProducts'
import { useSyncQueryCache } from './useSyncQueryCache'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
dayjs.extend(isBetween)
export const useDealStatusUpdateHook = (newDeal: NewDeal, productRows: Product[]) => {
  const { mutate: updateFields } = useUpdateFields()
  const { mutate: syncProducts } = useSyncProducts()
  const { syncProductCache, syncDealFields } = useSyncQueryCache()
  const { showSnackbar } = useSnackbarContext()
  const { mutate: createTimelineEntry } = useCreateTimeline()
  const { activeUser } = useActiveUser()
  const queryClient = useQueryClient()
  const handleDealStatusModalUpdate = ({
    churnNote,
    lastDate,
    reasonChurn,
    newStatus,
    onSuccess,
  }: {
    churnNote: string
    lastDate: string
    reasonChurn: string
    newStatus: string
    onSuccess?: () => void
  }) => {
    const fieldsToUpdate = [
      { fieldName: 'lastDateOfService', newValue: lastDate },
      { fieldName: 'reasonChurn', newValue: reasonChurn },
      { fieldName: 'churnNote', newValue: churnNote },
      { fieldName: 'dealStatus', newValue: newStatus },
      { fieldName: 'endDate', newValue: lastDate },
    ]

    const updates = fieldsToUpdate.map(({ fieldName, newValue }) => ({
      tableName: 'ORGANIZATIONS_TABLE',
      PK: newDeal.PK,
      SK: newDeal.SK,
      fieldName,
      newValue,
    }))

    const updatedProducts = productRows
      .filter(p => ['Active', 'Month To Month', 'In Progress', 'Ended'].includes(p.status))
      .map(p => {
        const today = dayjs()
        const start = dayjs(p?.startDate)
        const end = dayjs(lastDate)

        let newStatus = p.status
        if (today.isAfter(end)) {
          newStatus = 'Ended'
        } else if (today.isBetween(start, end, 'day', '[]')) {
          newStatus = 'Active'
        }

        return {
          ...p,
          endDate: lastDate,
          dateRange: [start.toISOString(), end.toISOString()],
          status: newStatus,
        }
      })

    console.log('updatedProducts', updatedProducts)
    const timelineData = updateDealTimelineData(
      newDeal?.deal_uuid,
      activeUser,
      'Update',
      'deals',
      `Deal Status Update`,
      `Deal status updated ${newStatus}`
    )
    updateFields(
      { updates },
      {
        onSuccess: () => {
          showSnackbar('Deal updated successfully', 'success')

          // ✅ Optional: Create timeline entry
          createTimelineEntry(timelineData)
          // Inside onSuccess
          syncProducts(
            {
              tableName: 'ORGANIZATIONS_TABLE',
              PK: newDeal.PK,
              deal_uuid: newDeal.deal_uuid,
              products: updatedProducts,
              deletedProducts: [],
            },
            {
              onSuccess: () => {
                console.log('✅ Products updated successfully on deal status change')
                syncProductCache(queryClient, updatedProducts)
              },
              onError: err => {
                console.error('❌ Failed to sync products:', err)
                showSnackbar('Failed to sync products', 'error')
              },
            }
          )

          syncDealFields(queryClient, newDeal, fieldsToUpdate)

          if (onSuccess) onSuccess()
        },
        onError: error => {
          console.error('Failed to update fields:', error)
          showSnackbar('Failed to update deal fields', 'error')
        },
      }
    )
  }

  return { handleDealStatusModalUpdate }
}
